import { initCompliance } from '@ms/yammer-compliance';
import { createStore } from '@ms/yammer-data';
import { getEtagFromStateOrPersistedState } from '@ms/yammer-data/dist/domains/ecs/selectors';
import {
  configureFeedThreadCountsForPersistedExperiment,
  setFeedConfigForThreadCountExperiment,
} from '@ms/yammer-data/dist/domains/feedConfig/actions';
import {
  loadFeedGroupLoadParams as preloadGroupFeedLoadParamsBundle,
  loadFeedHomeLoadParams as preloadHomeFeedLoadParamsBundle,
} from '@ms/yammer-data/dist/domains/feedLoad/actionParameters/lazyExports';
import { realtimeLogger } from '@ms/yammer-data/dist/realtime/debugEventLog/middleware';
import type { Store } from '@ms/yammer-data/dist/state/types';
import { i18nReducer as i18n } from '@ms/yammer-i18n-react';
import {
  endAndReportPerformanceMeasure,
  reportBootstrapNavigationAndResourceEvents,
  startMultiStepScenarioPerformanceMeasure,
  startPerformanceMeasure,
} from '@ms/yammer-telemetry';
import { isAuthRedirect, isMCASContext } from '@ms/yammer-web-authenticators';
import { loadIsExpectedError } from '@ms/yammer-web-support/dist/errors';
import { isEventsPage, isLoadedFromPWA, removePWAQuery } from '@ms/yammer-web-support/dist/location';
import type { Persistor } from 'redux-persist';

import type { YammerWebState } from '../../../state/types';
import { setupAuthenticator } from '../../auth';
import { redirectToMcasUrl } from '../../auth/redirectToMcasUrl';
import { setupCache } from '../../cache';
import config from '../../config';
import { persistConfig } from '../../persistedState';
import loadRenderer from '../3-render';

import {
  addTelemetryMiddleware,
  configureNetworkContext,
  configureSession,
  ensureTokensAcquired,
  lazyLoadBootstrapDismissablePrompts,
  lazyLoadBootstrapNetworkTopicSettings,
  lazyPreloadBroadcastEvent,
  lazyPreloadNavigationGroups,
  lazyPreloadPageData,
  loadFeatureAccessSettings,
  loadUserData,
  performHomeRedirect,
  replaceRootPathWithHomeFeedPath,
  savePersistKey,
  setLocaleFromBrowser,
  setLocaleFromUserData,
  setupDiagnosticsDebugLogging,
  setupFeatureFlighting,
  tryRedirectToEngageDomain,
  tryUpdateOptInPreference,
} from './bootstrapActions';
import {
  reportBootstrapCompleted,
  reportBootstrapStarted,
  reportErrorWithCode,
  reportStandaloneAppLaunched,
  setupLogging,
} from './bootstrapTelemetry';
import { isPermanentFailure } from './isPermanentFailure';
import initializeServiceWorker from './serviceWorker';
import { setupTelemetry } from './setupTelemetry';
import { setupTokenRepair } from './setupTokenRepair';

// eslint-disable-next-line max-statements
const bootstrapApp = async (): Promise<void> => {
  const measure = startPerformanceMeasure('bootstrap');
  const scenario = startMultiStepScenarioPerformanceMeasure({ scenario: 'bootstrap' });

  let endedWithError = false;
  let endedWithExpectedError = false;
  let interrupted = false;
  let store: Store<YammerWebState> | undefined;
  let persistor: Persistor | undefined;

  try {
    scenario.startStep(1, 'Remove PWA Query');
    const isPWA = isLoadedFromPWA();
    if (isPWA) {
      removePWAQuery();
    }
    scenario.endStep(1);

    scenario.startStep(2, 'Configure Telemetry');
    await setupTelemetry({
      isPWA,
    });
    scenario.endStep(2);

    scenario.startStep(3, 'Check for MCAS Redirect');
    if (isMCASContext()) {
      redirectToMcasUrl();
      interrupted = true;
      scenario.endStep(3);

      return;
    }
    scenario.endStep(3);

    scenario.startStep(4, 'Setup Cache');
    setupCache();
    scenario.endStep(4);

    scenario.startStep(5, 'Preload Feed Load Params Bundles');
    if (!isEventsPage(window.location.pathname)) {
      preloadHomeFeedLoadParamsBundle();
      preloadGroupFeedLoadParamsBundle();
    }
    scenario.endStep(5);

    scenario.startStep(6, 'Setup Authenticator');
    const authenticator = await setupAuthenticator();
    scenario.endStep(6);

    scenario.startStep(7, 'Initialize Authenticator Current Account');
    await authenticator.initializeCurrentAccount();
    scenario.endStep(7);

    scenario.startStep(8, 'Check for Auth Redirect');
    if (isAuthRedirect()) {
      interrupted = true;
      scenario.endStep(8);

      return;
    }
    scenario.endStep(8);

    scenario.startStep(9, 'Check For HomeFeed Redirect');
    replaceRootPathWithHomeFeedPath(window.location.pathname);
    scenario.endStep(9);

    scenario.startStep(10, 'Initialize Service Worker');
    initializeServiceWorker();
    scenario.endStep(10);

    scenario.startStep(11, 'Create Store');
    const createStoreResponse = createStore<YammerWebState>({
      reducers: { i18n },
      initialState: { suiteHeaderTheme: { isDefault: true } },
      middleware: [realtimeLogger],
      persistConfig,
    });
    store = createStoreResponse.store;
    persistor = createStoreResponse.persistor;
    const { dispatch, getState } = store;
    scenario.endStep(11);

    scenario.startStep(12, 'Kick Off Acquire Yammer Token');
    const ensureTokensAcquiredPromise = ensureTokensAcquired();
    scenario.endStep(12);

    scenario.startStep(13, 'Report Bootstrap Started');
    reportBootstrapStarted();
    scenario.endStep(13);

    scenario.startStep(14, 'Report Standalone App Launched');
    reportStandaloneAppLaunched();
    scenario.endStep(14);

    scenario.startStep(15, 'Save Persist Key');
    savePersistKey(dispatch);
    scenario.endStep(15);

    scenario.startStep(16, 'Add Telemetry Middleware');
    await addTelemetryMiddleware(getState);
    scenario.endStep(16);

    scenario.startStep(17, 'Configure Session');
    configureSession(dispatch);
    scenario.endStep(17);

    scenario.startStep(18, 'Setup Logging');
    setupLogging();
    scenario.endStep(18);

    scenario.startStep(19, 'Kick Off Configure Feed Thread Counts For Persisted Experiment');
    const feedConfigPromise = configureFeedThreadCountsForPersistedExperiment(dispatch, persistConfig.key);
    scenario.endStep(19);

    scenario.startStep(20, 'Configure Network Context');
    configureNetworkContext(dispatch);
    scenario.endStep(20);

    scenario.startStep(21, 'Await On Yammer Token And Feed Config');
    await Promise.all([ensureTokensAcquiredPromise, feedConfigPromise]);
    scenario.endStep(21);

    scenario.startStep(22, 'Kick Off CurrentUserClients');
    const userDataPromise = loadUserData(dispatch);
    scenario.endStep(22);

    scenario.startStep(23, 'Load ECS Experiments');
    const featureFlightingPromise = setupFeatureFlighting(dispatch, getState).then(() => {
      scenario.endStep(23);

      scenario.startStep(24, 'Set Feed Config For Thread Count Experiment');
      setFeedConfigForThreadCountExperiment(dispatch, getState);
      scenario.endStep(24);

      scenario.startStep(25, 'Kick Off FeatureAccessSettingsClients');
      const loadFeatureAccessSettingsPromise = loadFeatureAccessSettings(dispatch);
      scenario.endStep(25);

      return loadFeatureAccessSettingsPromise;
    });

    scenario.startStep(26, 'Preload Renderer Bundle');
    const rendererPromise = loadRenderer();
    scenario.endStep(26);

    scenario.startStep(27, 'Check If Live Events Page');
    const isLiveEventsPage = isEventsPage(window.location.pathname);
    scenario.endStep(27);

    scenario.startStep(28, 'Preload Page Data');
    if (!isLiveEventsPage) {
      lazyPreloadPageData(dispatch, getState);
    }
    scenario.endStep(28);

    scenario.startStep(29, 'Kick Off Bootstrap Network Topic Settings');
    lazyLoadBootstrapNetworkTopicSettings(dispatch, getState);
    scenario.endStep(29);

    scenario.startStep(30, 'Await ECS Experiments and CurrentUserClients');
    await Promise.all([featureFlightingPromise, userDataPromise]);
    scenario.endStep(30);

    scenario.startStep(31, 'Kick Off Preload Broadcast Event');
    if (isLiveEventsPage) {
      lazyPreloadBroadcastEvent(dispatch);
    }
    scenario.endStep(31);

    scenario.startStep(32, 'Try Redirect To Engage Domain');
    await tryRedirectToEngageDomain(dispatch, getState());
    scenario.endStep(32);

    scenario.startStep(33, 'Get ETag From State or Persisted State');
    const etagPromise = getEtagFromStateOrPersistedState(getState());
    scenario.endStep(33);

    scenario.startStep(34, 'Perform Home Network Redirect');
    performHomeRedirect(dispatch);
    scenario.endStep(34);

    scenario.startStep(35, 'Setup Diagnostics Debug Logging');
    setupDiagnosticsDebugLogging(getState);
    scenario.endStep(35);

    scenario.startStep(36, 'Init Ocps Policies');
    initCompliance(config.msal.resources.ocps, 'bootstrap_async');
    scenario.endStep(36);

    scenario.startStep(37, 'Set Locale From User Data');

    setLocaleFromUserData(dispatch, getState());
    scenario.endStep(37);

    scenario.startStep(38, 'Setup Token Repair');
    setupTokenRepair(getState());
    scenario.endStep(38);

    scenario.startStep(39, 'Kick Off Preload Navigation Groups and Dismissable Prompts');
    if (!isLiveEventsPage) {
      lazyPreloadNavigationGroups(dispatch);
      lazyLoadBootstrapDismissablePrompts(dispatch);
    }
    scenario.endStep(39);

    scenario.startStep(40, 'Await Etag and Renderer Bundle');
    const [etag, render] = await Promise.all([etagPromise, rendererPromise]);
    scenario.endStep(40);

    scenario.startStep(41, 'Render App Component');
    render({
      store,
      target: document.getElementById('root') as HTMLElement,
      isFailure: false,
      persistor,
      etag,
    });
    scenario.endStep(41);
  } catch (error) {
    endedWithError = true;
    reportErrorWithCode(error, 'BootstrapFailed');

    // istanbul ignore else
    if (store) {
      setLocaleFromBrowser(store.dispatch, store.getState());
    }

    if (isPermanentFailure(error)) {
      // istanbul ignore else
      if (store) {
        const [render, etag] = await Promise.all([loadRenderer(), getEtagFromStateOrPersistedState(store.getState())]);
        render({ store, target: document.getElementById('root') as HTMLElement, isFailure: true, etag });
      }
    }

    const { isExpectedError } = await loadIsExpectedError();
    endedWithExpectedError = isExpectedError(error);
  } finally {
    scenario.startStep(42, 'End Bootstrap Performance Measure');
    endAndReportPerformanceMeasure({
      ...measure,
      eventProperties: {
        ...measure.eventProperties,
        endedWithError,
        interrupted,
        isExpectedError: endedWithExpectedError,
      },
    });
    scenario.endStep(42);

    scenario.startStep(43, 'Report Bootstrap Navigation and Resource Events');
    reportBootstrapNavigationAndResourceEvents();
    scenario.endStep(43);

    scenario.startStep(44, 'Try Update Opt In Preference');
    // istanbul ignore else
    if (store) {
      tryUpdateOptInPreference(store.dispatch, store.getState());
    }
    scenario.endStep(44);

    scenario.startStep(45, 'Report Bootstrap Completed');
    // istanbul ignore else
    if (store) {
      reportBootstrapCompleted(store.getState().suiteHeaderTheme);
    }
    scenario.endStep(45);

    scenario.endAndReport({ endedWithError, interrupted });
  }
};

export default bootstrapApp;
